import { normalizeCmrExtractionResult } from '../services/cmrExtractionService';
import investorOnboardingService from '../services/investorOnboardingService';
import { clearAllCmrFilesFromIndexedDB } from './cmrUploadFileStore';

export const STATE_KEY = 'investor_onboarding_client_state';
export const STATE_VERSION = 1;
export const ONBOARDING_STATE_EVENT = 'investor-onboarding-state-change';

/** @deprecated legacy keys — migrated once then removed */
const LEGACY_LS_SESSION_KEY = 'investor_onboarding_session_uuid';
const LEGACY_SS_TYPE_KEY = 'investor_onboarding_type';
const LEGACY_SS_CMR_EXTRACTION_KEY = 'ifa_investor_onboarding_cmr_extraction';
const LEGACY_SS_CMR_STEP_KEY = 'ifa_investor_onboarding_cmr_step';

export const INVESTOR_ONBOARDING_TYPES = {
  MANUAL: 'manual',
  AUTOMATED: 'automated',
};

export const CMR_SUB_STEPS = {
  UPLOAD: 'upload',
  PARTNER: 'partner',
};

function emptyState() {
  return {
    version: STATE_VERSION,
    sessionUuid: '',
    onboardingType: '',
    cmrSubStep: CMR_SUB_STEPS.UPLOAD,
    cmrExtraction: null,
    updatedAt: Date.now(),
  };
}

function normalizeState(raw) {
  const base = emptyState();
  if (!raw || typeof raw !== 'object') return base;

  const type = raw.onboardingType;
  const validType =
    type === INVESTOR_ONBOARDING_TYPES.MANUAL || type === INVESTOR_ONBOARDING_TYPES.AUTOMATED
      ? type
      : '';

  const subStep = raw.cmrSubStep;
  const validSubStep =
    subStep === CMR_SUB_STEPS.UPLOAD || subStep === CMR_SUB_STEPS.PARTNER
      ? subStep
      : CMR_SUB_STEPS.UPLOAD;

  let cmrExtraction = null;
  if (raw.cmrExtraction && typeof raw.cmrExtraction === 'object') {
    const normalized = normalizeCmrExtractionResult(raw.cmrExtraction);
    cmrExtraction = Object.keys(normalized).length > 0 ? normalized : null;
  }

  return {
    version: STATE_VERSION,
    sessionUuid: String(raw.sessionUuid ?? '').trim(),
    onboardingType: validType,
    cmrSubStep: validSubStep,
    cmrExtraction,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : Date.now(),
  };
}

let migrationDone = false;

function migrateLegacyStorage() {
  if (migrationDone) return;
  migrationDone = true;

  try {
    const existingRaw = localStorage.getItem(STATE_KEY);
    if (existingRaw) return;

    const legacyUuid = localStorage.getItem(LEGACY_LS_SESSION_KEY) || '';
    const legacyType = sessionStorage.getItem(LEGACY_SS_TYPE_KEY) || '';
    const legacyCmrRaw = sessionStorage.getItem(LEGACY_SS_CMR_EXTRACTION_KEY) || '';
    const legacyCmrStep = sessionStorage.getItem(LEGACY_SS_CMR_STEP_KEY) || '';

    const hasLegacy =
      legacyUuid || legacyType || legacyCmrRaw || legacyCmrStep;
    if (!hasLegacy) return;

    let cmrExtraction = null;
    if (legacyCmrRaw) {
      try {
        cmrExtraction = normalizeCmrExtractionResult(JSON.parse(legacyCmrRaw));
        if (Object.keys(cmrExtraction).length === 0) cmrExtraction = null;
      } catch {
        cmrExtraction = null;
      }
    }

    const migrated = normalizeState({
      sessionUuid: legacyUuid,
      onboardingType: legacyType,
      cmrSubStep: legacyCmrStep || CMR_SUB_STEPS.UPLOAD,
      cmrExtraction,
      updatedAt: Date.now(),
    });

    localStorage.setItem(STATE_KEY, JSON.stringify(migrated));
    removeLegacyKeys();
  } catch {
    /* ignore */
  }
}

function removeLegacyKeys() {
  try {
    localStorage.removeItem(LEGACY_LS_SESSION_KEY);
    sessionStorage.removeItem(LEGACY_SS_TYPE_KEY);
    sessionStorage.removeItem(LEGACY_SS_CMR_EXTRACTION_KEY);
    sessionStorage.removeItem(LEGACY_SS_CMR_STEP_KEY);
  } catch {
    /* ignore */
  }
}

function notifyStateChange() {
  try {
    window.dispatchEvent(new CustomEvent(ONBOARDING_STATE_EVENT));
  } catch {
    /* ignore */
  }
}

export function readOnboardingState() {
  migrateLegacyStorage();
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return emptyState();
    return normalizeState(JSON.parse(raw));
  } catch {
    return emptyState();
  }
}

export function writeOnboardingState(state) {
  const normalized = normalizeState(state);
  normalized.updatedAt = Date.now();
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(normalized));
    notifyStateChange();
  } catch {
    /* ignore */
  }
  return normalized;
}

export function patchOnboardingState(partial) {
  const current = readOnboardingState();
  return writeOnboardingState({ ...current, ...partial });
}

export function clearLocalOnboardingState() {
  try {
    localStorage.removeItem(STATE_KEY);
    removeLegacyKeys();
  } catch {
    /* ignore */
  }
  clearAllCmrFilesFromIndexedDB().catch(() => {});
  notifyStateChange();
}

export async function discardOnboardingSession(options = {}) {
  const { cancelRemote = true, showErrorToast = null } = options;
  const state = readOnboardingState();
  const uuid = state.sessionUuid;

  if (cancelRemote && uuid) {
    try {
      await investorOnboardingService.cancelSession(uuid);
    } catch (err) {
      if (showErrorToast) {
        showErrorToast(
          err?.response?.data?.message || err?.message || 'Could not cancel onboarding session on server.'
        );
      }
    }
  }

  clearLocalOnboardingState();
}

// ─── Session UUID ───────────────────────────────────────────────────────────

export function getStoredInvestorSessionUuid() {
  return readOnboardingState().sessionUuid;
}

export function setStoredInvestorSessionUuid(uuid) {
  patchOnboardingState({ sessionUuid: uuid ? String(uuid) : '' });
}

export function clearStoredInvestorSessionUuid() {
  patchOnboardingState({ sessionUuid: '' });
}

// ─── Onboarding type ────────────────────────────────────────────────────────

export function getStoredInvestorOnboardingType() {
  return readOnboardingState().onboardingType;
}

export function setStoredInvestorOnboardingType(type) {
  if (!type) {
    patchOnboardingState({ onboardingType: '' });
    return;
  }
  patchOnboardingState({ onboardingType: String(type) });
}

export function inferInvestorOnboardingType() {
  const state = readOnboardingState();
  if (state.onboardingType) return state.onboardingType;
  return state.cmrExtraction
    ? INVESTOR_ONBOARDING_TYPES.AUTOMATED
    : INVESTOR_ONBOARDING_TYPES.MANUAL;
}

export function ensureInvestorOnboardingTypeForResume() {
  const state = readOnboardingState();
  if (state.onboardingType) return state.onboardingType;
  const inferred = inferInvestorOnboardingType();
  patchOnboardingState({ onboardingType: inferred });
  return inferred;
}

export function clearStoredInvestorOnboardingType() {
  patchOnboardingState({ onboardingType: '' });
}

// ─── CMR extraction ─────────────────────────────────────────────────────────

export function readStoredCmrExtraction() {
  return readOnboardingState().cmrExtraction;
}

export function persistCmrExtraction(data) {
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    patchOnboardingState({
      cmrExtraction: normalizeCmrExtractionResult(data),
    });
  } else {
    patchOnboardingState({ cmrExtraction: null });
  }
}

export function getStoredCmrSubStep() {
  return readOnboardingState().cmrSubStep;
}

export function setStoredCmrSubStep(step) {
  patchOnboardingState({
    cmrSubStep: step || CMR_SUB_STEPS.UPLOAD,
  });
}

export function getMobileFromCmrExtraction(extraction) {
  const source = extraction ?? readStoredCmrExtraction();
  return normalizeCmrExtractionResult(source).mobile || '';
}

export function clearCmrFieldsFromState() {
  patchOnboardingState({
    cmrExtraction: null,
    cmrSubStep: CMR_SUB_STEPS.UPLOAD,
  });
}

// ─── Composite helpers ────────────────────────────────────────────────────────

export function clearInvestorOnboardingStorage() {
  clearLocalOnboardingState();
}

/**
 * Clear local wizard state and set new onboarding type.
 * Used when picking Manual/Automated on method selection.
 *
 * Note: does NOT cancel the session remotely — `start-mobile` will resume
 * an existing IN_PROGRESS session naturally. Remote cancel is only done from
 * InvestorListPage after the flow is fully complete or explicitly abandoned.
 */
export async function startFreshOnboardingType(type, options = {}) { // eslint-disable-line no-unused-vars
  clearLocalOnboardingState();
  if (type) {
    patchOnboardingState({
      onboardingType: type,
      sessionUuid: '',
      cmrExtraction: null,
      cmrSubStep: CMR_SUB_STEPS.UPLOAD,
    });
  }
}

export function backToMethodSelection() {
  patchOnboardingState({
    onboardingType: '',
    cmrExtraction: null,
    cmrSubStep: CMR_SUB_STEPS.UPLOAD,
  });
}

export function subscribeOnboardingState(listener) {
  if (typeof window === 'undefined') return () => {};

  const onStorage = (e) => {
    if (e.key === STATE_KEY || e.key === null) listener(readOnboardingState());
  };
  const onCustom = () => listener(readOnboardingState());

  window.addEventListener('storage', onStorage);
  window.addEventListener(ONBOARDING_STATE_EVENT, onCustom);

  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(ONBOARDING_STATE_EVENT, onCustom);
  };
}
